import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Shared state for the app-wide AI side panel.
//
// The panel is mounted once in Layout so it survives route changes, but the
// content it shows is page-specific. Rather than have the panel import from
// every container, each page *registers* what it wants the AI to work with —
// a title, the text/context of the current record, and any page-specific
// quick actions — and unregisters on unmount. The panel renders whatever the
// currently-mounted page last registered, or a neutral empty state if nothing
// has.
//
// This keeps the panel itself generic: adding AI to a new screen means calling
// useAiPanelContext() there, not editing this component.

const AiPanelContext = createContext(null);

const AiPanelProvider = ({ children }) => {
	const [isOpen, setIsOpen] = useState(false);
	// What the current page has registered. `null` means no page has provided
	// anything, which the panel renders as an empty state rather than a broken
	// or half-configured UI.
	const [pageContext, setPageContext] = useState(null);

	const openPanel = useCallback(() => setIsOpen(true), []);
	const closePanel = useCallback(() => setIsOpen(false), []);
	const togglePanel = useCallback(() => setIsOpen((open) => !open), []);

	// Called by pages (via useAiPanelContext) to publish their context. Kept as
	// a plain setter rather than a merge so a page fully owns what the panel
	// shows while it is mounted.
	const registerPageContext = useCallback((context) => setPageContext(context), []);
	const clearPageContext = useCallback(() => setPageContext(null), []);

	const value = useMemo(
		() => ({
			isOpen,
			openPanel,
			closePanel,
			togglePanel,
			pageContext,
			registerPageContext,
			clearPageContext,
			isAvailable: true,
		}),
		[isOpen, openPanel, closePanel, togglePanel, pageContext, registerPageContext, clearPageContext],
	);

	return <AiPanelContext.Provider value={value}>{children}</AiPanelContext.Provider>;
};

// No-op fallback for components that render both inside and outside the
// provider — Header is shared with LayoutPublic, and an unauthenticated page
// crashing on a missing provider would be a worse failure than the AI button
// simply doing nothing there.
const NOOP_PANEL = {
	isOpen: false,
	openPanel: () => null,
	closePanel: () => null,
	togglePanel: () => null,
	pageContext: null,
	registerPageContext: () => null,
	clearPageContext: () => null,
	isAvailable: false,
};

// Returns the panel controls. Check `isAvailable` if a component needs to hide
// its AI affordance entirely when no provider is present.
function useAiPanel() {
	const ctx = useContext(AiPanelContext);
	return ctx || NOOP_PANEL;
}

export { AiPanelProvider, useAiPanel };
export default AiPanelContext;
